import { inForceListOn } from 'pages/pricelists/modal/matterPricelistEditorUtils';
import { Employment } from 'types/employees';
import { HourlyRate, MatterPricelist, PersonalRate, PricingKind } from 'types/pricelists';

// Of the employments covering the date, the one that started last; an open-ended row covers every date
// after its start.
export function findEmploymentOnDate(employmentHistory: Employment[], date: Date): Employment | undefined {
    const validEmployments = employmentHistory.filter((item) => {
        const validFrom = item.validFrom ? new Date(item.validFrom) : new Date('1900-01-01');
        const validTo = item.validTo ? new Date(item.validTo) : new Date('9999-01-01');
        return date >= validFrom && date <= validTo;
    });

    return validEmployments.toSorted((a, b) => {
        const dateA = a.validFrom ? new Date(a.validFrom).getTime() : 0;
        const dateB = b.validFrom ? new Date(b.validFrom).getTime() : 0;
        return dateB - dateA;
    })[0];
}

// The author's own row on the price list wins over their job role's, mirroring how the backend prices
// the saved timecard.
export function resolveHourlyPrice(
    authorId: number,
    jobRoleId: number | null | undefined,
    rates?: HourlyRate[],
    personalRates?: PersonalRate[]
): string | null | undefined {
    const personalRate = personalRates?.find((rate) => rate.employee.id === authorId);
    if (personalRate) {
        return personalRate.hourlyPrice;
    }
    return rates?.find((rate) => rate.jobRole.id === jobRoleId)?.hourlyPrice;
}

// The price the backend stores for the employee's work on the case on an ISO date: the list covering
// that date, and inside it the employee's own row, else the row of the role they held on that date. An
// employee-priced case has no role prices to fall back on: a person without a row is unpriced, null.
// Undefined until the case's lists are loaded, so a caller can tell "not priced" from "not yet known".
export function resolveHourlyPriceOn(
    pricelists: MatterPricelist[] | undefined,
    employeeId: number,
    employmentHistory: Employment[] | undefined,
    date: string
): string | null | undefined {
    if (!pricelists) {
        return undefined;
    }
    const pricelist = inForceListOn(pricelists, date);
    if (pricelist?.pricingKind === PricingKind.EMPLOYEE) {
        return pricelist.personalRates.find((rate) => rate.employee.id === employeeId)?.hourlyPrice ?? null;
    }
    const jobRoleId = findEmploymentOnDate(employmentHistory ?? [], new Date(date))?.jobRoleId;
    return resolveHourlyPrice(employeeId, jobRoleId, pricelist?.rates, pricelist?.personalRates);
}
